import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useDebounce } from '@/hooks/useDebounce';
import { useSkuCatalogInfinite } from '../../hooks';
import type { SkuCatalogItem } from '../../api';
import type { Language } from '../../types';

interface SkuCatalogComboboxProps {
  value?: string;
  language: Language;
  onSelect: (sku: SkuCatalogItem) => void;
  disabled?: boolean;
  className?: string;
}

export function SkuCatalogCombobox({
  value,
  language,
  onSelect,
  disabled = false,
  className,
}: SkuCatalogComboboxProps) {
  const { t } = useTranslation('quotes');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const infiniteQuery = useSkuCatalogInfinite(debouncedSearch);

  const loadedSkus = useMemo(
    () => infiniteQuery.data?.pages.flatMap((page) => page.skus) ?? [],
    [infiniteQuery.data],
  );
  const total = infiniteQuery.data?.pages[0]?.total ?? 0;

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          infiniteQuery.hasNextPage &&
          !infiniteQuery.isFetchingNextPage
        ) {
          void infiniteQuery.fetchNextPage();
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [
    open,
    infiniteQuery.hasNextPage,
    infiniteQuery.isFetchingNextPage,
    infiniteQuery.fetchNextPage,
  ]);

  const getSkuName = (item: SkuCatalogItem) => {
    if (typeof item.name === 'object' && item.name !== null) {
      return language === 'fr'
        ? (item.name.fr || item.name.en || '')
        : (item.name.en || item.name.fr || '');
    }
    return language === 'fr' ? (item.name_fr || item.name || '') : (item.name || '');
  };

  const showingLabel = total > 0
    ? t('sku.showingCount', { loaded: loadedSkus.length, total })
    : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'h-8 w-full justify-between text-xs font-normal',
            className,
          )}
        >
          <span className="truncate">
            {value || t('sku.selectSku')}
          </span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start" onWheel={(e) => e.stopPropagation()}>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('sku.searchSku')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {infiniteQuery.isError
                ? t('sku.loadFailed')
                : infiniteQuery.isLoading
                  ? t('sku.loading')
                  : t('sku.noSkuFound')}
            </CommandEmpty>
            <CommandGroup>
              {loadedSkus.map((sku) => (
                <CommandItem
                  key={`${sku._id}-${sku.sku}`}
                  value={`${sku.sku} ${getSkuName(sku)}`}
                  onSelect={() => {
                    onSelect(sku);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === sku.sku ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  {sku.sku} - {getSkuName(sku)}
                </CommandItem>
              ))}
              {infiniteQuery.hasNextPage && (
                <div
                  ref={sentinelRef}
                  className="flex items-center justify-center py-2 text-xs text-muted-foreground"
                >
                  {infiniteQuery.isFetchingNextPage ? (
                    <>
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                      {t('sku.loadingMore')}
                    </>
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </div>
              )}
            </CommandGroup>
          </CommandList>
          {showingLabel && (
            <div className="border-t px-2 py-1 text-xs text-muted-foreground">
              {showingLabel}
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
